import { GrowthBookClient, setPolyfills } from '@growthbook/growthbook'
import { EventSource } from 'eventsource'

import { NETWORK_TIMEOUT_MS } from '../constants'

const CLIENT_KEY = process.env.GROWTHBOOK_CLIENT_KEY || ''
const API_HOST = process.env.GROWTHBOOK_API_HOST || 'http://localhost:3300'

const REFRESH_INTERVAL_MS = 5 * 60 * 1000
const INITIAL_RETRY_DELAY_MS = 3 * 1000
const MAX_RETRY_DELAY_MS = 60 * 1000
const MAX_RETRY_JITTER_MS = 1 * 1000

// The SDK reads EventSource off its own polyfills object, which defaults to
// `globalThis.EventSource` — only available from Node 22 on. Once we run on
// Node >= 22, drop this call and the `eventsource` dependency with it.
setPolyfills({ EventSource })

let _clientPromise: Promise<GrowthBookClient> | null = null

export function getClient(): Promise<GrowthBookClient> {
  if (!_clientPromise) {
    _clientPromise = initializeClient().catch((e) => {
      _clientPromise = null
      console.error('[shared-feature-flags] initializeClient rejected unexpectedly', e)
      return new GrowthBookClient({ apiHost: API_HOST, clientKey: CLIENT_KEY })
    })
  }
  return _clientPromise
}

function schedulePeriodicRefresh(client: GrowthBookClient): void {
  let lastDateUpdated = client.getDecryptedPayload().dateUpdated

  setInterval(async () => {
    try {
      const dateUpdated = client.getDecryptedPayload().dateUpdated
      if (dateUpdated !== lastDateUpdated) {
        lastDateUpdated = dateUpdated
        return
      }

      await client.refreshFeatures()
      lastDateUpdated = client.getDecryptedPayload().dateUpdated
    } catch (e) {
      console.error('[shared-feature-flags] refreshFeatures failed', e)
    }
  }, REFRESH_INTERVAL_MS)
}

async function tryInit(client: GrowthBookClient): Promise<Error | null> {
  try {
    const response = await client.init({ timeout: NETWORK_TIMEOUT_MS, streaming: true })
    if (response?.error) {
      return response.error
    }
    return null
  } catch (e) {
    if (e instanceof Error) {
      return e
    }
    return new Error(`[shared-feature-flags] unknown error initializing GrowthBook client: ${e}`)
  }
}

async function retryInitInBackground(client: GrowthBookClient) {
  let attempt = 1
  let initialized = false
  while (!initialized) {
    const delay = Math.min(MAX_RETRY_DELAY_MS, INITIAL_RETRY_DELAY_MS * 2 ** (attempt - 1))
    const jitter = Math.random() * MAX_RETRY_JITTER_MS
    await new Promise((resolve) => setTimeout(resolve, delay + jitter))

    const error = await tryInit(client)
    if (!error) {
      schedulePeriodicRefresh(client)
      initialized = true
    } else {
      console.error('[shared-feature-flags] client init failed', error)
      attempt++
    }
  }
}

async function initializeClient(): Promise<GrowthBookClient> {
  const client = new GrowthBookClient({
    apiHost: API_HOST,
    clientKey: CLIENT_KEY,
  })

  if (!CLIENT_KEY) {
    console.warn(
      '[shared-feature-flags] GROWTHBOOK_CLIENT_KEY is not set; serving declared defaults only'
    )
    return client
  }

  const error = await tryInit(client)
  if (!error) {
    schedulePeriodicRefresh(client)
    return client
  }

  // Resolve with the un-initialized client so callers aren't blocked on a
  // GrowthBook outage, and keep retrying in the background.
  console.error('[shared-feature-flags] client init failed', error)
  void retryInitInBackground(client)
  return client
}
